// Stock and purchasing rules (technical design §6.10): moving average cost, the morning picklist,
// the weekly reorder, and the 3-way match of a vendor bill to its PO and receipts. Quantities are
// exact decimals in thousandths (numeric(12,3)); costs are cents with four decimals. Pure.

import { formatDecimal, parseDecimal, roundScaled } from '@flowology/kernel';

/** Quantities as integer thousandths, so stock arithmetic is exact. */
export const qty = (s: string | number): bigint => {
  const d = parseDecimal(s);
  if (d.scale > 3) throw new RangeError(`quantity ${s} has more than 3 decimals`);
  return d.units * 10n ** BigInt(3 - d.scale);
};
export const qtyText = (milli: bigint): string => formatDecimal({ units: milli, scale: 3 });
/** Unit costs as integer ten-thousandths of a cent. */
export const unitCost = (s: string | number): bigint => {
  const d = parseDecimal(s);
  if (d.scale > 4) throw new RangeError(`cost ${s} has more than 4 decimals`);
  return d.units * 10n ** BigInt(4 - d.scale);
};
export const unitCostText = (u: bigint): string => formatDecimal({ units: u, scale: 4 });

/**
 * A quantity bought in boxes, in the item's own unit (truck-stock parity 4): boxes × units per box,
 * exactly. Never rounded: a product past thousandths is refused (RangeError).
 */
export function toUseUnits(purchaseQty: string, unitsPerPurchase: string): string {
  const milli = qty(purchaseQty) * qty(unitsPerPurchase);
  if (milli % 1000n !== 0n)
    throw new RangeError(`${purchaseQty} × ${unitsPerPurchase} is finer than a thousandth`);
  return qtyText(milli / 1000n);
}

/** The whole boxes that cover a quantity in the item's own unit (rounded up: a reorder never falls short). */
export function wholePurchaseUnits(useQty: string, unitsPerPurchase: string): string {
  const q = qty(useQty);
  if (q <= 0n) return qtyText(0n);
  const per = qty(unitsPerPurchase);
  return qtyText(((q + per - 1n) / per) * 1000n);
}

/** A box's cost per unit of the item (cents, four decimals, half up): what the moving average takes in. */
export function costPerUseUnit(purchaseCost: string | number, unitsPerPurchase: string): string {
  const per = qty(unitsPerPurchase);
  return unitCostText((2n * unitCost(purchaseCost) * 1000n + per) / (2n * per));
}

/** quantity × unit cost in cents, rounded half up once. */
export function stockValueCents(quantity: string, cost: string): number {
  return Number(roundScaled(qty(quantity) * unitCost(cost), 7, 'half_up'));
}

/**
 * The moving average after receiving `inQty` at `inCost`. When stock on hand is zero or negative
 * (usage ran ahead of receipts), the new cost is simply the receipt's cost.
 */
export function movingAverage(onHand: string, avgCost: string, inQty: string, inCost: string): string {
  const oh = qty(onHand);
  const iq = qty(inQty);
  if (iq <= 0n) return avgCost;
  if (oh <= 0n) return unitCostText(unitCost(inCost));
  const total = oh * unitCost(avgCost) + iq * unitCost(inCost);
  const den = oh + iq;
  return unitCostText((2n * total + den) / (2n * den)); // half up; both positive here
}

/**
 * The moving average after taking back `outQty` that came in at `outCost` (a receipt voided, BL-214):
 * the receipt undone. With nothing left after it, or less value than it took, it stays as it was.
 */
export function movingAverageOut(onHand: string, avgCost: string, outQty: string, outCost: string): string {
  const oh = qty(onHand);
  const oq = qty(outQty);
  if (oq <= 0n || oh - oq <= 0n) return avgCost;
  const total = oh * unitCost(avgCost) - oq * unitCost(outCost);
  if (total < 0n) return avgCost;
  const den = oh - oq;
  return unitCostText((2n * total + den) / (2n * den)); // half up; both positive here
}

// ── picklists ──

export interface PickInput {
  itemId: string;
  onHand: string; // on the truck now
  min: string;
  max: string;
  usedYesterday: string;
}

export interface PickLine {
  itemId: string;
  quantity: string;
  why: 'used_yesterday' | 'below_min';
}

/**
 * The morning picklist for one truck (technical design §6.10): the larger of what it used
 * yesterday (up to its maximum, when it has one) and, when it is below its minimum, what fills it to
 * its maximum.
 */
export function buildPicklist(items: readonly PickInput[]): PickLine[] {
  const out: PickLine[] = [];
  for (const i of items) {
    const oh = qty(i.onHand);
    // what it used, but never past its maximum: a truck refilled since (a count's order) isn't overfilled
    const room = qty(i.max) > 0n ? qty(i.max) - oh : null;
    const usedAll = qty(i.usedYesterday) > 0n ? qty(i.usedYesterday) : 0n;
    const used = room === null || usedAll <= room ? usedAll : room > 0n ? room : 0n;
    const fill = oh < qty(i.min) ? qty(i.max) - oh : 0n;
    const q = fill > used ? fill : used;
    if (q > 0n)
      out.push({ itemId: i.itemId, quantity: qtyText(q), why: fill > used ? 'below_min' : 'used_yesterday' });
  }
  return out;
}

/** Splits the warehouse's stock across the day's picklists in truck order; the rest is short. */
export function allocateWarehouse(
  warehouseOnHand: string,
  demands: readonly { picklistId: string; quantity: string }[],
): { picklistId: string; available: string; short: string }[] {
  let left = qty(warehouseOnHand);
  return demands.map((d) => {
    const want = qty(d.quantity);
    const give = left > 0n ? (want < left ? want : left) : 0n;
    left -= give;
    return { picklistId: d.picklistId, available: qtyText(give), short: qtyText(want - give) };
  });
}

// ── reorder ──

/**
 * What the weekly reorder buys for one item at the warehouse: enough to bring it back to its
 * maximum, counting what is already on order, once it is at or below its minimum.
 */
export function reorderQuantity(r: { onHand: string; onOrder: string; min: string; max: string }): string {
  const position = qty(r.onHand) + qty(r.onOrder);
  if (position > qty(r.min)) return '0.000';
  const need = qty(r.max) - position;
  return qtyText(need > 0n ? need : 0n);
}

// ── 3-way match ──

export interface MatchLineInput {
  lineNo: number;
  billedQty: string;
  billedUnitCents: number;
  /** Already approved on this bill (an earlier approval of what had arrived). */
  approvedQty: string;
  po: { unitCostCents: number; orderedQty: string; receivedQty: string; billedElsewhereQty: string } | null;
}

export interface MatchLineResult {
  lineNo: number;
  /** Quantity approvable now: received and not billed on another bill, less what is approved here. */
  approvableQty: string;
  /** Billed but not received: held until the goods or a credit note arrive (Q30). */
  heldQty: string;
  priceVarianceCents: number; // per unit: billed − PO cost
  status: 'matched' | 'short' | 'price_variance' | 'short_and_price' | 'no_po';
}

/**
 * Compares a bill to its PO and what was received. Over-billing (more than received) is held, not
 * refused; a price difference is flagged and must be accepted at approval.
 */
export function threeWayMatch(lines: readonly MatchLineInput[]): MatchLineResult[] {
  return lines.map((l) => {
    const billed = qty(l.billedQty);
    const approved = qty(l.approvedQty);
    if (!l.po)
      return {
        lineNo: l.lineNo,
        approvableQty: qtyText(billed - approved),
        heldQty: '0.000',
        priceVarianceCents: 0,
        status: 'no_po',
      };
    const receivable = qty(l.po.receivedQty) - qty(l.po.billedElsewhereQty);
    const canApprove = (billed < receivable ? billed : receivable) - approved;
    const approvable = canApprove > 0n ? canApprove : 0n;
    const held = billed - approved - approvable;
    const variance = l.billedUnitCents - l.po.unitCostCents;
    const short = held > 0n;
    return {
      lineNo: l.lineNo,
      approvableQty: qtyText(approvable),
      heldQty: qtyText(held),
      priceVarianceCents: variance,
      status:
        short && variance ? 'short_and_price' : short ? 'short' : variance ? 'price_variance' : 'matched',
    };
  });
}

/**
 * How many items one truck count lists (a whole truck can carry more than 500): the server refuses
 * more in these words, and the iPad says them before Send.
 */
export const STOCK_COUNT_MAX_LINES = 2000;
export const STOCK_COUNT_LINES_SAID = `A count can list ${String(STOCK_COUNT_MAX_LINES)} items at most.`;

/**
 * Whether material entered on a job is too late for the technician (truck-stock parity 6): their last
 * visit on it ended more than `days` days before they entered it; then the office adds it. The entry
 * counts never earlier than that visit's start, so a clock set back gains nothing. The server
 * (materials.use and .return) and the iPad (Work's Use and Return) ask the same rule.
 */
export function materialTooLate(a: {
  /** The latest end, and start, of the technician's visits on the job; null with none. */
  lastEnd: string | Date | null;
  lastStart: string | Date | null;
  /** When they entered it: the device's time, never after it reached the server. */
  entered: string | Date;
  days: number;
}): boolean {
  if (a.lastEnd === null) return false;
  const ms = (x: string | Date) => (typeof x === 'string' ? Date.parse(x) : x.getTime());
  const entered = Math.max(ms(a.entered), a.lastStart === null ? -Infinity : ms(a.lastStart));
  return entered - ms(a.lastEnd) > a.days * 86_400_000;
}
